import { useCallback, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { classNames } from '@framework/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePopupRef } from '@framework/hooks';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { FontSize } from '@model-framework/text';

type Props = {
  onChangeTextSize(newFontSize: FontSize): void;
  onOpenSubMenu(menuType: string): void;
};

export const TextSizeMenuButton = forwardRef<
  {
    closePopup: () => void;
  },
  Props
>(({ onChangeTextSize, onOpenSubMenu }, ref) => {
  const { t } = useTranslation();
  const { isOpen, popupRef, handleToggle, handleClose } = usePopupRef<HTMLDivElement>(false);

  useImperativeHandle(ref, () => ({
    closePopup: () => handleClose(),
  }));

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      onOpenSubMenu('textSize');
      handleToggle();
    },
    [handleToggle, onOpenSubMenu]
  );

  return (
    <div className="relative hover:bg-gray-200">
      <button
        // data-right-click-menu は親 item をクリックしても閉じないようにするためのマーカー
        data-right-click-menu
        className={classNames('flex w-full cursor-pointer px-8 py-4')}
        onClick={handleClick}
      >
        <div className="inline-block pl-4 pr-6">{t('canvas.menu.textSize')}</div>
        <FontAwesomeIcon icon={faAngleRight} className="ml-auto" />
      </button>
      <div
        className={classNames(
          'absolute left-full top-0 z-50 w-max rounded-lg bg-white py-4 shadow-2xl',
          isOpen ? 'block' : 'hidden'
        )}
        ref={popupRef}
      >
        <button
          className={'flex w-full cursor-pointer px-8 py-4 hover:bg-gray-200 focus:outline-none'}
          onClick={() => onChangeTextSize(FontSize.XXL)}
        >
          <div className="inline-block pl-2">{t('canvas.zone.textSize.max')}</div>
        </button>
        <button
          className={'flex w-full cursor-pointer px-8 py-4 hover:bg-gray-200 focus:outline-none'}
          onClick={() => onChangeTextSize(FontSize.XL)}
        >
          <div className="inline-block pl-2">{t('canvas.node.textSize.large')}</div>
        </button>
        <button
          className={'flex w-full cursor-pointer px-8 py-4 hover:bg-gray-200 focus:outline-none'}
          onClick={() => onChangeTextSize(FontSize.L)}
        >
          <div className="inline-block pl-2">{t('canvas.node.textSize.medium')}</div>
        </button>
        <button
          className={'flex w-full cursor-pointer px-8 py-4 hover:bg-gray-200 focus:outline-none'}
          onClick={() => onChangeTextSize(FontSize.M)}
        >
          <div className="inline-block pl-2">{t('canvas.node.textSize.small')}</div>
        </button>
        <button
          className={'flex w-full cursor-pointer px-8 py-4 hover:bg-gray-200 focus:outline-none'}
          onClick={() => onChangeTextSize(FontSize.S)}
        >
          <div className="inline-block pl-2">{t('canvas.zone.textSize.min')}</div>
        </button>
      </div>
    </div>
  );
});

TextSizeMenuButton.displayName = 'TextSizeMenuButton';
